import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Sparkles,
  Archive,
  MonitorSmartphone,
  BellRing,
  HeartPulse,
  Package,
  Plug,
  Radar,
  LineChart,
  FileDown,
  GraduationCap,
  BookOpen,
  Handshake,
  WifiOff,
  Languages,
  Cable,
  PlayCircle,
} from 'lucide-react';
import { Button, Container, Eyebrow, GradientText, IconTile, Reveal, SectionHeading, TextLink, CheckList } from '@/components/ui';
import { CtaBand, TrainingBand } from '@/components/sections';
import { DashboardMockup, FleetMockup } from '@/components/mockups';
import { PromoVideo } from '@/components/PromoVideo';
import { usePageTitle } from '@/components/layout/SiteLayout';
import { PLATFORM_MODULES, findModule } from '@/content/platform';
import { INDUSTRIES } from '@/content/industries';
import { MAX_PARTNER_DISCOUNT } from '@/content/training';
import { INK_U_URL, STATS, orderLink } from '@/lib/site';

const VENDORS = [
  'Allen-Bradley',
  'Siemens',
  'Fanuc',
  'Omron',
  'Mitsubishi',
  'GE / Emerson',
  'AutomationDirect',
  'Universal Robots',
  'KUKA',
  'Cognex',
  'Keyence',
  'SICK',
  'MTConnect',
  'OPC-UA',
  'Modbus',
  'MQTT Sparkplug B',
  'SECS/GEM',
];

const PILLARS = [
  {
    title: 'Connect',
    body: 'Every PLC, CNC, robot and camera on one appliance — discovered automatically.',
    modules: ['connectivity', 'vision', 'security'],
  },
  {
    title: 'Visualize & Automate',
    body: 'Dashboards operators use, and alarms and logic that act without waiting for them.',
    modules: ['visualization', 'alarms', 'logic'],
  },
  {
    title: 'Analyze & Execute',
    body: 'OEE, SPC and reports on top of the historian, the MES layer to run production, and AI that reads the data with you.',
    modules: ['analytics', 'mes', 'smart-assist'],
  },
];

const CLOUD = [
  { icon: Sparkles, color: '#8b5cf6', title: 'Smart Assist AI', body: 'AI tag classification, alarm suggestions and page design.', anchor: 'ai' },
  { icon: Archive, color: '#16a34a', title: 'Data Archive', body: 'An off-site copy of your history with up to 7-year retention.', anchor: 'archive' },
  { icon: MonitorSmartphone, color: '#3b82f6', title: 'Remote Viewing', body: 'Dashboards, custom pages and reports from anywhere.', anchor: 'remote' },
  { icon: BellRing, color: '#dc2626', title: 'SMS & Voice Alerts', body: 'Twilio-powered alarm texts and phone calls.', anchor: 'alerts' },
  { icon: HeartPulse, color: '#06b6d4', title: 'Fleet Health', body: 'Every connected appliance’s health at a glance — free.', anchor: 'fleet' },
];

const STEPS = [
  { icon: Package, title: 'Order', body: 'Tell us your machines. We quote the appliance, software and services.' },
  { icon: Plug, title: 'Plug in', body: 'It arrives preconfigured. Mount it, connect Ethernet, power on.' },
  { icon: Radar, title: 'Discover', body: 'The Network Scanner finds your devices; Smart Assist maps the tags.' },
  { icon: LineChart, title: 'Run', body: 'Dashboards, alarms and OEE live in hours — not a months-long project.' },
];

export function HomePage() {
  usePageTitle('');
  return (
    <>
      {/* ── Hero ── */}
      <section className="relative overflow-hidden bg-white">
        <div className="absolute inset-0 bg-grid-light [mask-image:radial-gradient(ellipse_at_top_left,black,transparent_70%)]" />
        <div className="absolute top-20 -right-40 w-[700px] h-[700px] rounded-full bg-brand-secondary/10 blur-[140px]" />
        <div className="absolute -bottom-40 left-1/4 w-[600px] h-[500px] rounded-full bg-brand-primary/10 blur-[140px]" />
        <Container className="relative pt-14 pb-20 lg:pt-20 lg:pb-28">
          <div className="grid lg:grid-cols-[1.1fr_1fr] gap-14 items-center">
            <div>
              <Link
                to="/cloud"
                className="inline-flex items-center gap-2 pl-1 pr-3 py-1 rounded-full bg-white border border-glass-border text-xs font-medium text-text-secondary hover:border-brand-primary/40 transition-colors shadow-sm"
              >
                <span className="px-2 py-0.5 rounded-full bg-gradient-to-r from-brand-primary to-brand-secondary text-white font-semibold">New</span>
                Scada.Ink Cloud — AI, archive, remote viewing & alerts
                <ArrowRight size={12} />
              </Link>
              <h1 className="mt-7 text-4xl sm:text-5xl lg:text-[3.6rem] font-extrabold leading-[1.05] tracking-tight">
                The industrial platform for plants that <GradientText>can’t wait on the cloud</GradientText>
              </h1>
              <p className="mt-6 text-lg sm:text-xl text-text-secondary max-w-xl leading-relaxed">
                Scada.Ink puts SCADA, historian, analytics and MES on a preconfigured appliance that runs on your network with no internet at
                all. Add cloud services only where they help.
              </p>
              <div className="mt-9 flex flex-col sm:flex-row gap-3">
                <Button to={orderLink('quote')} size="lg" arrow>
                  Get a quote
                </Button>
                <Button to={orderLink('demo')} size="lg" variant="secondary">
                  <PlayCircle size={16} /> Book a demo
                </Button>
              </div>
              <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-xl">
                {[
                  { icon: Cable, label: `${STATS.drivers} native drivers` },
                  { icon: WifiOff, label: 'Zero internet required' },
                  { icon: GraduationCap, label: 'Free certification training' },
                  { icon: Languages, label: `${STATS.languages} languages` },
                ].map((f) => (
                  <div key={f.label} className="flex items-center gap-2 text-xs font-medium text-text-secondary">
                    <f.icon size={16} className="text-brand-primary shrink-0" />
                    {f.label}
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="animate-float-slow">
                <DashboardMockup />
              </div>
              <div className="hidden sm:flex absolute -left-6 -top-7 glass-panel px-3.5 py-2.5 items-center gap-2.5 shadow-lg">
                <span className="w-8 h-8 rounded-lg bg-brand-secondary/12 text-brand-secondary flex items-center justify-center">
                  <Sparkles size={16} />
                </span>
                <div>
                  <div className="text-[11px] font-bold">Smart Assist</div>
                  <div className="text-[10px] text-text-muted">214 tags classified</div>
                </div>
              </div>
              <div className="hidden sm:flex absolute -right-4 -bottom-8 glass-panel px-3.5 py-2.5 items-center gap-2.5 shadow-lg">
                <span className="w-8 h-8 rounded-lg bg-status-fault/12 text-status-fault flex items-center justify-center">
                  <BellRing size={16} />
                </span>
                <div>
                  <div className="text-[11px] font-bold">Alarm escalated</div>
                  <div className="text-[10px] text-text-muted">SMS → voice call</div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </section>

      {/* ── Trust strip ── */}
      <section className="bg-white border-y border-glass-border py-7 overflow-hidden">
        <Container>
          <p className="text-center text-xs font-semibold uppercase tracking-[0.18em] text-text-muted mb-5">
            Connects natively to the equipment you already own
          </p>
        </Container>
        <div className="relative [mask-image:linear-gradient(to_right,transparent,black_10%,black_90%,transparent)]">
          <div className="flex w-max animate-marquee gap-12 pr-12">
            {[...VENDORS, ...VENDORS].map((v, i) => (
              <span key={i} className="text-lg font-bold text-slate-400 whitespace-nowrap">
                {v}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── Promo video ── */}
      <section className="py-20 lg:py-24">
        <Container>
          <SectionHeading
            align="center"
            eyebrow="See it in 30 seconds"
            title={
              <>
                From machine data to <GradientText>decisions</GradientText>, on one appliance
              </>
            }
          />
          <Reveal>
            <PromoVideo />
          </Reveal>
        </Container>
      </section>

      {/* ── Stats band ── */}
      <section className="relative bg-navy-900 overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark" />
        <Container className="relative py-14">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              [STATS.drivers, 'Native protocol drivers', 'PLCs, CNCs, robots, cameras'],
              [STATS.internet, 'Internet connections required', 'Your data stays on your network'],
              [String(STATS.courses), 'Free Ink U courses', `${STATS.videos} videos with quizzes`],
              [`${MAX_PARTNER_DISCOUNT}%`, 'Max partner discount', 'For doubly certified partners'],
            ].map(([n, label, sub]) => (
              <Reveal key={label}>
                <div className="text-4xl sm:text-5xl font-extrabold bg-gradient-to-r from-white to-brand-accent bg-clip-text text-transparent">{n}</div>
                <div className="mt-2 text-sm font-semibold text-white">{label}</div>
                <div className="text-xs text-slate-400 mt-0.5">{sub}</div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* ── Three pillars ── */}
      <section className="py-20 lg:py-28">
        <Container>
          <SectionHeading
            eyebrow="One platform"
            title={
              <>
                Everything between the machine and the <GradientText>management report</GradientText>
              </>
            }
            intro="Replace a patchwork of gateways, historians, HMI licenses and spreadsheets with one appliance your team can learn in a week."
            action={
              <Button to="/platform" variant="secondary" arrow>
                Explore the platform
              </Button>
            }
          />
          <div className="grid lg:grid-cols-3 gap-6">
            {PILLARS.map((p, i) => (
              <Reveal key={p.title} delay={i * 100}>
                <div className="glass-panel h-full p-7 hover:shadow-lg transition-shadow">
                  <div className="text-xs font-bold text-brand-primary">0{i + 1}</div>
                  <h3 className="mt-2 text-2xl font-bold">{p.title}</h3>
                  <p className="mt-2 text-sm text-text-secondary leading-relaxed">{p.body}</p>
                  <div className="mt-6 space-y-1 border-t border-glass-border pt-4">
                    {p.modules.map((slug) => {
                      const m = findModule(slug)!;
                      return (
                        <Link
                          key={slug}
                          to={`/platform/${slug}`}
                          className="group flex items-center gap-3 rounded-lg p-2 -mx-2 hover:bg-ink-muted transition-colors"
                        >
                          <IconTile icon={m.icon} color={m.color} size="sm" />
                          <span className="flex-1 min-w-0">
                            <span className="block text-sm font-semibold group-hover:text-brand-primary transition-colors">{m.name}</span>
                            <span className="block text-xs text-text-muted truncate">{m.summary}</span>
                          </span>
                          <ArrowRight size={14} className="text-text-muted group-hover:text-brand-primary group-hover:translate-x-0.5 transition-all" />
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* ── AI showcase ── */}
      <section className="py-20 lg:py-24 bg-white border-y border-glass-border">
        <Container>
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <Reveal className="order-2 lg:order-1">
              <FleetMockup />
            </Reveal>
            <Reveal delay={100} className="order-1 lg:order-2">
              <Eyebrow>Scada.Ink Cloud</Eyebrow>
              <h2 className="mt-4 text-3xl sm:text-4xl font-bold tracking-tight">Edge-first. Cloud when you want it.</h2>
              <p className="mt-4 text-lg text-text-secondary leading-relaxed">
                The appliance never needs the internet. When it has a connection, Scada.Ink Cloud adds AI, off-site archiving, remote viewing,
                SMS and voice alerts, and fleet health — all subscribed to and managed from the customer portal.
              </p>
              <div className="mt-7 grid sm:grid-cols-2 gap-3">
                {CLOUD.map((c) => (
                  <Link key={c.title} to={`/cloud#${c.anchor}`} className="group glass-card p-4 flex items-start gap-3 hover:shadow-md hover:border-brand-primary/30 transition-all">
                    <IconTile icon={c.icon} color={c.color} size="sm" />
                    <span>
                      <span className="block text-sm font-semibold group-hover:text-brand-primary">{c.title}</span>
                      <span className="block text-xs text-text-secondary mt-0.5 leading-snug">{c.body}</span>
                    </span>
                  </Link>
                ))}
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button to="/pricing#cloud" arrow>
                  See cloud plans
                </Button>
                <Button to="/register" variant="secondary">
                  Create a portal account
                </Button>
              </div>
            </Reveal>
          </div>
        </Container>
      </section>

      <TrainingBand />

      {/* ── Industries ── */}
      <section className="py-20 lg:py-28">
        <Container>
          <SectionHeading
            eyebrow="Industries"
            title="Built for the floors that make things"
            intro="From a single CNC cell to a multi-plant operation, the same appliance adapts to the way your industry runs."
            action={<TextLink to="/industries">All industries</TextLink>}
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {INDUSTRIES.map((ind, i) => (
              <Reveal key={ind.slug} delay={i * 50}>
                <Link to={`/industries#${ind.slug}`} className="group glass-card h-full p-5 flex flex-col hover:shadow-lg hover:-translate-y-0.5 transition-all">
                  <ind.icon size={22} className="text-brand-primary" />
                  <div className="mt-4 font-semibold group-hover:text-brand-primary transition-colors">{ind.name}</div>
                  <p className="mt-1.5 text-xs text-text-secondary leading-relaxed flex-1">{ind.solution}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-brand-primary">
                    Learn more <ArrowRight size={12} />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>

      {/* ── How it works ── */}
      <section className="py-20 bg-white border-y border-glass-border">
        <Container>
          <SectionHeading eyebrow="Getting started" title="From order to live dashboards in days" align="center" />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 relative">
            <div className="hidden lg:block absolute top-7 left-[12%] right-[12%] h-px bg-gradient-to-r from-brand-primary/10 via-brand-primary/40 to-brand-secondary/10" />
            {STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 100} className="relative text-center">
                <div className="relative mx-auto w-14 h-14 rounded-2xl bg-white border border-glass-border shadow-md flex items-center justify-center text-brand-primary">
                  <s.icon size={24} />
                  <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-gradient-to-br from-brand-primary to-brand-secondary text-white text-[11px] font-bold flex items-center justify-center">
                    {i + 1}
                  </span>
                </div>
                <h3 className="mt-5 font-semibold">{s.title}</h3>
                <p className="mt-2 text-sm text-text-secondary leading-relaxed max-w-[15rem] mx-auto">{s.body}</p>
              </Reveal>
            ))}
          </div>
          <div className="mt-12 text-center">
            <Button to={orderLink('order')} size="lg" arrow>
              Start your order
            </Button>
          </div>
        </Container>
      </section>

      {/* ── Partners + resources ── */}
      <section className="py-20 lg:py-28">
        <Container>
          <div className="grid lg:grid-cols-[1fr_1.4fr] gap-6">
            <Reveal>
              <div className="relative h-full overflow-hidden rounded-[var(--radius-glass-lg)] bg-navy-900 p-8 lg:p-10 text-white">
                <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-brand-secondary/30 blur-3xl" />
                <div className="absolute -left-10 -bottom-20 w-72 h-72 rounded-full bg-brand-primary/30 blur-3xl" />
                <div className="relative">
                  <Handshake size={28} className="text-brand-accent" />
                  <div className="mt-5 text-xs font-semibold uppercase tracking-[0.18em] text-brand-accent">Resellers & integrators</div>
                  <h3 className="mt-3 text-3xl font-bold leading-tight">
                    Get certified. Save up to {MAX_PARTNER_DISCOUNT}% on every license.
                  </h3>
                  <p className="mt-4 text-slate-300 leading-relaxed">
                    Partners who complete both Ink U certifications buy Scada.Ink software at a discount that grows with sales volume.
                  </p>
                  <div className="mt-7 flex flex-wrap gap-3">
                    <Button to="/partners" variant="light" arrow>
                      Partner program
                    </Button>
                    <Button to="/partners#apply" variant="outline-light">
                      Apply now
                    </Button>
                  </div>
                </div>
              </div>
            </Reveal>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { icon: FileDown, title: 'Platform overview (PDF)', body: 'The whole platform on a few pages, for sharing internally.', cta: 'Download', href: '/overview.pdf' },
                { icon: BookOpen, title: 'Feature datasheets', body: 'One-page sheets on dashboards, reporting, ladder logic and more.', cta: 'Browse', to: '/resources' },
                { icon: GraduationCap, title: 'Ink U course catalog', body: `${STATS.courses} courses from first login to MES and appliance administration.`, cta: 'View courses', to: '/training#courses' },
                { icon: HeartPulse, title: 'Customer portal', body: 'Subscriptions, SMS & voice settings and appliance health in one place.', cta: 'Log in', to: '/login' },
              ].map((r, i) => (
                <Reveal key={r.title} delay={i * 80}>
                  <div className="glass-card h-full p-6 flex flex-col">
                    <IconTile icon={r.icon} color="#3b82f6" />
                    <h3 className="mt-4 font-semibold">{r.title}</h3>
                    <p className="mt-1.5 text-sm text-text-secondary leading-relaxed flex-1">{r.body}</p>
                    <div className="mt-4">
                      {r.href ? (
                        <a href={r.href} download className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-primary hover:gap-2.5 transition-all">
                          {r.cta} <ArrowRight size={14} />
                        </a>
                      ) : (
                        <TextLink to={r.to!}>{r.cta}</TextLink>
                      )}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* ── Why edge-first ── */}
      <section className="pb-20 lg:pb-28">
        <Container>
          <div className="glass-panel p-8 lg:p-12 grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <Eyebrow>Why Scada.Ink</Eyebrow>
              <h2 className="mt-4 text-3xl font-bold tracking-tight">Monitor your factory floor, not your cloud bill</h2>
              <p className="mt-4 text-text-secondary leading-relaxed">
                Cloud-only platforms stop when the internet does and charge for every tag you send them. Scada.Ink runs where the machines are,
                and the cloud is an option you add — never a dependency.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button to={orderLink('quote')} arrow>
                  Get a quote
                </Button>
                <Button href={INK_U_URL} variant="secondary">
                  Try the training first
                </Button>
              </div>
            </div>
            <CheckList
              items={[
                'Keeps running through internet and WAN outages',
                'Unlimited devices and tags on every appliance',
                'No per-tag or per-client licensing',
                'AI features work even on air-gapped appliances',
                'Your data never leaves the building unless you choose',
                `Modules for ${PLATFORM_MODULES.length} areas, from connectivity to MES`,
              ]}
            />
          </div>
        </Container>
      </section>

      <CtaBand />
    </>
  );
}
